import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import Button from '../button/Button';
import Link from '../link/Link';

import { RangeInput } from '../input/Input';
import OutsideAlerter from '../outsidealerter/OutsideAlerter';

import es from 'date-fns/locale/es';
import SelectableChips from '../selectablechips/SelectableChips';
import H from '../texts/h/H';
import * as S from './DatePicker.styles';
import { FilterAlternativeProps } from './DatePicker';
import { parse, isValid } from 'date-fns';

const RESET_VALUE = [null, null];

const getInitialValue = (range, value) =>
  value ? (range ? value : [value]) : [];

const defaultProps = {
  dateFormat: 'yyyy-MM-dd',
  outputFormat: 'yyyy-MM-dd',
};

type DatePickerProps = {
  onChange?: (date: Date) => void;
  dateFormat?: string;
  outputFormat?: string;
  range?: boolean;
  filterAlternatives?: Array<FilterAlternativeProps>;
  setCurrentFilterAlternative?;
  currentFilterAlternative?: FilterAlternativeProps;
  bookingDate?: boolean;
  disabled?: boolean;
  numberOfMonths?: number;
  value?;
  label?: string;
  placeholder?: string;
  id?: string;
  children?: any;
  defaultValue: string;
  displayFormat?: 'DD/MM/AA' | 'yyyy-MM-dd' | 'MMMM yyyy' | 'dd-MM-yyyy' | 'dd/MM/yyyy';
  alignLeft?: boolean;
  withoutElevation?: boolean;
};

const DatePicker = (props: DatePickerProps) => {
  const {
    onChange,
    dateFormat,
    outputFormat,
    range,
    filterAlternatives,
    setCurrentFilterAlternative,
    currentFilterAlternative,
    bookingDate,
    disabled,
    numberOfMonths,
    defaultValue,
    placeholder: customPlaceholder,
    displayFormat = 'yyyy-MM-dd',
    alignLeft = false,
    withoutElevation = false,
  } = props;
  const initialValue = getInitialValue(range, props.value);
  const [value, setValue] = useState(initialValue);
  const [inputValue, setInputValue] = useState(() =>
    defaultValue && defaultValue !== '' ? formatDateToPicker(defaultValue) : ''
  );
  const lastValueRef = useRef(initialValue);
  const inputEl = useRef(null);

  const setLastValue = (lastValue) => {
    lastValueRef.current = lastValue;
  };

  function formatDateToPicker(dateStr: string) {
    if (!dateStr) return '';
    const ymd = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!ymd) {
      return dateStr;
    }
    const [, year, month, day] = ymd;

    if (displayFormat === 'DD/MM/AA') {
      return `${day}/${month}/${year.slice(-2)}`;
    }

    if (displayFormat === 'MMMM yyyy') {
      const date = parse(`${year}-${month}-01`, 'yyyy-MM-dd', new Date());
      if (isValid(date)) {
        const formatted = format(date, 'MMMM yyyy', { locale: es });
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
      }
      return dateStr;
    }

    if (displayFormat === 'dd-MM-yyyy') {
      return `${day}-${month}-${year}`;
    }
    if (displayFormat === 'dd/MM/yyyy') {
      return `${day}/${month}/${year}`;
    }
    
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    if (defaultValue && defaultValue !== '') {
      setInputValue(formatDateToPicker(defaultValue));
    } else {
      setInputValue('');
    }
  }, [defaultValue]);

  useEffect(() => {
    if (value !== props.value) {
      setValue(props.value || []);
    }
  }, [props.value]);

  const [startDate, endDate] = value;

  const [open, setOpen] = useState(false);

  const outputFormatDate = (date) => (date ? format(date, outputFormat) : date);

  const handleClose = () => setOpen(false);

  const handleApply = () => {
    handleClose();
    if (range) {
      onChange && onChange(value.map(outputFormatDate));
    } else {
      onChange && onChange(value.map(outputFormatDate)[0]);
    }
    setLastValue(value);
  };

  const handleCancel = () => {
    setValue(lastValueRef.current);
    handleClose();
  };

  const handleOnChange = ({ startDate, endDate }) => {
    setValue([startDate, endDate]);
    if (startDate) {
      if (displayFormat === 'DD/MM/AA') {
        setInputValue(format(startDate, 'dd/MM/yy'));
      } else if (displayFormat === 'MMMM yyyy') {
        const formatted = format(startDate, 'MMMM yyyy', { locale: es });
        setInputValue(formatted.charAt(0).toUpperCase() + formatted.slice(1));
      } else if (displayFormat === 'dd-MM-yyyy') {
        setInputValue(format(startDate, 'dd-MM-yyyy'));
      } else if (displayFormat === 'dd/MM/yyyy') {
        setInputValue(format(startDate, 'dd/MM/yyyy'));
      } else {
        setInputValue(format(startDate, 'yyyy-MM-dd'));
      }
      if (!range) {
        onChange && onChange(outputFormatDate(startDate));
        setLastValue([startDate, endDate]);
        handleClose();
      }
    } else {
      setInputValue('');
    }
  };

  const handleReset = () => {
    setInputValue('');
    onChange && onChange(null);
    setValue(RESET_VALUE);
    setLastValue(RESET_VALUE);
    handleClose();
  };

  const startFormatted = startDate
    ? `${format(startDate, dateFormat, { locale: es })}${range ? ' - ' : ''}`
    : '';
  const endFormatted = endDate
    ? format(endDate, dateFormat, { locale: es })
    : '';

  const handleSelected = (filterAlternatives) =>
    filterAlternatives.map((item) =>
      item.id === currentFilterAlternative
        ? { ...item, active: true }
        : { ...item, active: false }
    );

  const filtersAlternativesRender = () => (
    <S.DatePickerOptions data-testid='datepicker-filter-alternatives'>
      <H type='6' align='left' variant='neutral'>
        Seleccioná tipo de fecha
      </H>

      <SelectableChips
        options={handleSelected(filterAlternatives)}
        direction='column'
        onChange={(list) => {
          setValue(RESET_VALUE);
          setCurrentFilterAlternative(list.filter((item) => item.active)[0].id);
        }}
      />
    </S.DatePickerOptions>
  );

  const handleManualInputChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
    
    const isFullYearDayFirst = displayFormat === 'dd-MM-yyyy' || displayFormat === 'dd/MM/yyyy';
    if (displayFormat === 'DD/MM/AA' || isFullYearDayFirst) {
      let day = value.slice(0, 2);
      let month = value.slice(2, 4);
      let year = value.slice(4, 8);
      const separator = displayFormat === 'dd-MM-yyyy' ? '-' : '/';
      
      if (!value) {
        setInputValue('');
        setValue([null, null]);
        onChange && onChange(null);
        return;
      }
      
      if (day.length === 2) {
        const dayNum = parseInt(day, 10);
        if (dayNum < 1 || dayNum > 31) return;
      }
      
      if (month.length === 2) {
        const monthNum = parseInt(month, 10);
        if (monthNum < 1 || monthNum > 12) return;
      }
      
      if (year.length === 4) {
        const yearNum = parseInt(year, 10);
        if (yearNum < 1950) return;
      }
      
      let formatted = day;
      if (month.length) formatted += separator + month;
      if (isFullYearDayFirst) {
        if (year.length) formatted += separator + year;
      } else {
        if (year.length >= 2) formatted += separator + year.slice(-2);
      }
      
      setInputValue(formatted);
      
      const expectedLength = isFullYearDayFirst ? 10 : 8;
      if (formatted.length === expectedLength) {
        const fullYear = year.length === 2 ? `20${year}` : year;
        const date = parse(`${day}/${month}/${fullYear}`, 'dd/MM/yyyy', new Date());
        if (isValid(date)) {
          setValue([date, null]);
          onChange && onChange(format(date, outputFormat));
          return;
        }
      }
    } else {
      let year = value.slice(0, 4);
      let month = value.slice(4, 6);
      let day = value.slice(6, 8);
      
      if (!value) {
        setInputValue('');
        setValue([null, null]);
        onChange && onChange(null);
        return;
      }
      
      if (month.length === 2) {
        const monthNum = parseInt(month, 10);
        if (monthNum < 1 || monthNum > 12) return;
      }
      
      if (day.length === 2) {
        const dayNum = parseInt(day, 10);
        if (dayNum < 1 || dayNum > 31) return;
      }
      
      if (year.length === 4) {
        const yearNum = parseInt(year, 10);
        if (yearNum < 1950) return;
      }
      
      let formatted = year;
      if (month.length) formatted += '/' + month;
      if (day.length) formatted += '/' + day;
      
      setInputValue(formatted);
      
      if (formatted.length === 10) {
        const date = parse(formatted, 'yyyy/MM/dd', new Date());
        if (isValid(date)) {
          setValue([date, null]);
          onChange && onChange(format(date, outputFormat));
          return;
        }
      }
    }
    
    setValue([null, null]);
    onChange && onChange(null);
  };

  return (
    <>
      <OutsideAlerter
        handleOutSideClick={handleCancel}
        exclude={['#daterange-picker']}
        active={open}
      >
        <S.DatePickerAnchor>
          <RangeInput
            from={capitalize(startFormatted)}
            to={capitalize(endFormatted)}
            placeholder={customPlaceholder}
            value={inputValue}
            label={props.label}
            id={props.id}
            onChange={handleManualInputChange}
            inputRef={inputEl}
            icons={{
              iconRight: 'calendar_outline',
              onIconClick: () => setOpen(true),
            }}
            disabled={disabled}
          />
          {open && inputEl.current && (
            <DatePickerContainerPortal target={inputEl} alignLeft={alignLeft}>
              <S.DatePickerWrapper $flat={withoutElevation}>
                {filterAlternatives && filtersAlternativesRender()}
                <S.DatePickerCalendar>
                  <S.DatePickerCalendarControl>
                    {React.cloneElement(React.Children.only(props.children), {
                      onChange: handleOnChange,
                      value: { startDate, endDate },
                    })}
                  </S.DatePickerCalendarControl>

                  <S.DatePickerFooter
                    bookingDate={bookingDate}
                    numberOfMonths={numberOfMonths}
                  >
                    <S.DatePickerButtons>
                      <Link variant='info' size='sm' onClick={handleReset}>
                        Limpiar
                      </Link>
                      <S.DatePickerButtonsRight>
                        <Button
                          variant='secondary'
                          size='md'
                          onClick={handleCancel}
                        >
                          Cancelar
                        </Button>
                        <Button variant='primary' size='md' onClick={handleApply}>
                          Aplicar
                        </Button>
                      </S.DatePickerButtonsRight>
                    </S.DatePickerButtons>
                  </S.DatePickerFooter>
                </S.DatePickerCalendar>
              </S.DatePickerWrapper>
            </DatePickerContainerPortal>
          )}
        </S.DatePickerAnchor>
      </OutsideAlerter>
    </>
  );
};

const DatePickerContainerPortal = ({ children, target, alignLeft = false }: { children: any, target: any, alignLeft?: boolean }) => {
  const input = target.current;

  // Coordenadas estáticas calculadas UNA vez al abrir desde offset metrics del
  // input (offsetTop+offsetHeight+4 / left:0 / width:offsetWidth). Sin
  // getBoundingClientRect ni scroll listeners → cero drift: el popper absoluto
  // scrollea nativamente con su ancla dentro del contenedor de scroll.
  const targetProps = {
    top: input.offsetTop + input.offsetHeight + 4,
    left: 0,
    width: input.offsetWidth,
  };

  return <S.DatePickerPopper id='daterange-picker' {...targetProps} alignLeft={alignLeft}>{children}</S.DatePickerPopper>;
};

const capitalize = (str) => {
  const lower = str.toLowerCase();
  return str.charAt(0).toUpperCase() + lower.slice(1);
};

DatePicker.defaultProps = defaultProps;

export default DatePicker;